<script setup>
import { nextTick, onMounted, computed, ref, toRefs, watch } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useStore } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import FallbackBubble from './bubbles/Fallback.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useBranding } from 'shared/composables/useBranding';
import PmaWorkbenchActionsAPI from 'dashboard/api/pmaWorkbenchActions';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  call: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const isReviewEditorOpen = ref(false);
const isReviewSending = ref(false);
const isReviewSent = ref(false);
const reviewDraft = ref('');
const messageReviewBusinessContactDraft = ref({});
const reviewEditor = ref(null);
const isToolReviewEditorOpen = ref(false);
const isToolReviewSending = ref(false);
const isToolReviewCompleted = ref(false);
const toolReviewAction = ref('');
const toolReviewDraft = ref({});
const toolReviewEditor = ref(null);
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isPmaAiReviewPending = computed(() => {
  if (isReviewSent.value) return false;

  const attrs = props.additionalAttributes || {};
  return (
    attrs.pmaMessageReviewStatus === 'needs_human_review' ||
    attrs.pma_message_review_status === 'needs_human_review'
  );
});

const pmaAttr = (snakeKey, camelKey) => {
  const attrs = props.additionalAttributes || {};
  return attrs[camelKey] ?? attrs[snakeKey];
};

const fieldValue = (object, snakeKey, camelKey) => {
  if (!object || typeof object !== 'object') return undefined;
  return object[snakeKey] ?? object[camelKey];
};

const TOOL_REVIEW_CONTACT_RESOLUTION_KIND = 'contact_resolution';
const TOOL_REVIEW_CONTACT_RESOLUTION_SYSTEM_FIELD_NAMES = new Set([
  'business_source_label',
  'business_source_url',
  'business_recipient_timezone',
]);

const messageReviewBusinessContactPreview = computed(() => {
  const preview = pmaAttr(
    'pma_message_review_business_contact_preview',
    'pmaMessageReviewBusinessContactPreview'
  );
  return preview && typeof preview === 'object' ? preview : {};
});

const messageReviewBusinessContactFields = computed(() => {
  const raw = fieldValue(
    messageReviewBusinessContactPreview.value,
    'editable_fields',
    'editableFields'
  );
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(field => field && typeof field === 'object' && field.name)
    .map(field => ({
      name: String(field.name),
      label: String(field.label || field.name),
      value: String(field.value ?? ''),
      multiline: Boolean(field.multiline),
      sensitive: Boolean(field.sensitive),
    }));
});

const shouldSaveMessageReviewBusinessContact = computed(() => {
  return (
    String(
      messageReviewBusinessContactDraft.value
        .save_as_callable_business_contact ?? ''
    ).toLowerCase() === 'true'
  );
});

const toolReviewStatus = computed(() => {
  return String(pmaAttr('pma_tool_review_status', 'pmaToolReviewStatus') || '');
});

const isPmaToolReviewPending = computed(() => {
  return !isToolReviewCompleted.value && toolReviewStatus.value === 'pending';
});

const toolReviewEditableFields = computed(() => {
  const raw = pmaAttr(
    'pma_tool_review_editable_fields',
    'pmaToolReviewEditableFields'
  );
  if (!Array.isArray(raw)) return [];

  const draftPreview =
    pmaAttr('pma_tool_review_draft_preview', 'pmaToolReviewDraftPreview') || {};
  const isContactResolution =
    String(fieldValue(draftPreview, 'recovery_kind', 'recoveryKind') || '') ===
    TOOL_REVIEW_CONTACT_RESOLUTION_KIND;

  return raw
    .filter(field => field && typeof field === 'object' && field.name)
    .filter(field => {
      if (!isContactResolution) return true;

      return !TOOL_REVIEW_CONTACT_RESOLUTION_SYSTEM_FIELD_NAMES.has(
        String(field.name)
      );
    })
    .map(field => ({
      name: String(field.name),
      label: String(field.label || field.name),
      value: String(field.value ?? ''),
      multiline: Boolean(field.multiline),
      sensitive: Boolean(field.sensitive),
    }));
});

const toolReviewDraftPreview = computed(() => {
  const preview = pmaAttr(
    'pma_tool_review_draft_preview',
    'pmaToolReviewDraftPreview'
  );
  return preview && typeof preview === 'object' ? preview : {};
});

const toolReviewToolName = computed(() => {
  return String(
    pmaAttr('pma_tool_review_tool_name', 'pmaToolReviewToolName') || ''
  );
});

const isToolReviewContactResolution = computed(() => {
  return (
    String(
      fieldValue(
        toolReviewDraftPreview.value,
        'recovery_kind',
        'recoveryKind'
      ) || ''
    ) === 'contact_resolution'
  );
});

const shouldOpenToolReviewEditorByDefault = computed(() => {
  return (
    isPmaToolReviewPending.value &&
    isToolReviewContactResolution.value &&
    toolReviewEditableFields.value.length > 0
  );
});

const toolReviewEditSubmitActionType = computed(() => {
  return isToolReviewContactResolution.value
    ? 'va_contact_resolution_submit'
    : 'va_review_edit_send';
});

const toolReviewDisplayName = computed(() => {
  const raw = String(
    fieldValue(toolReviewDraftPreview.value, 'display_name', 'displayName') ||
      fieldValue(toolReviewDraftPreview.value, 'title', 'title') ||
      ''
  ).trim();
  if (raw) return raw;

  const toolName = toolReviewToolName.value.split('.').pop() || 'Action';
  return toolName.replaceAll('_', ' ');
});

const hasToolReviewDraftChanges = computed(() => {
  return toolReviewEditableFields.value.some(field => {
    return String(toolReviewDraft.value[field.name] ?? '') !== field.value;
  });
});

const isToolReviewEditSendDisabled = computed(() => {
  if (isToolReviewSending.value) {
    return true;
  }
  if (isToolReviewContactResolution.value) {
    const businessPhoneNumber = String(
      toolReviewDraft.value.business_phone_number ?? ''
    ).trim();
    return !businessPhoneNumber;
  }
  if (!hasToolReviewDraftChanges.value) {
    return true;
  }
  const body = String(toolReviewDraft.value.body ?? '').trim();
  return (
    Object.prototype.hasOwnProperty.call(toolReviewDraft.value, 'body') && !body
  );
});

const trimmedReviewDraft = computed(() => reviewDraft.value.trim());

const isReviewSendDisabled = computed(() => {
  if (isReviewSending.value || !trimmedReviewDraft.value) return true;
  if (!shouldSaveMessageReviewBusinessContact.value) return false;

  const business = String(
    messageReviewBusinessContactDraft.value.business ?? ''
  ).trim();
  const phoneNumber = String(
    messageReviewBusinessContactDraft.value.phone_number ?? ''
  ).trim();
  return !business || !phoneNumber;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (fileType === ATTACHMENT_TYPES.FALLBACK) return FallbackBubble;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

async function openReviewEditor() {
  reviewDraft.value = props.content || '';
  messageReviewBusinessContactDraft.value =
    messageReviewBusinessContactFields.value.reduce(
      (draft, field) => ({ ...draft, [field.name]: field.value }),
      {}
    );
  isReviewEditorOpen.value = true;
  await nextTick();
  reviewEditor.value?.focus();
}

function closeReviewEditor() {
  if (isReviewSending.value) return;

  isReviewEditorOpen.value = false;
  reviewDraft.value = '';
  messageReviewBusinessContactDraft.value = {};
}

function reviewSendFailureMessage(error) {
  const payload = error?.response?.data?.error;
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  return t('CONVERSATION.PMA_REVIEW_SEND_FAILED');
}

async function sendReviewedMessage() {
  if (isReviewSendDisabled.value) return;

  isReviewSending.value = true;
  try {
    const metadata = {};
    const sensitivePayload = {};
    if (shouldSaveMessageReviewBusinessContact.value) {
      messageReviewBusinessContactFields.value.forEach(field => {
        const value = String(
          messageReviewBusinessContactDraft.value[field.name] ?? ''
        );
        if (field.sensitive) {
          sensitivePayload[field.name] = value;
        } else {
          metadata[field.name] = value;
        }
      });
    }
    const payload = {
      chatwoot_conversation_id: props.conversationId,
      chatwoot_message_id: props.id,
      action_type: 'message_review_send',
      content: trimmedReviewDraft.value,
      idempotency_key: `chatwoot-review:${props.id}`,
      metadata,
    };
    if (Object.keys(sensitivePayload).length) {
      payload.sensitive_payload = sensitivePayload;
    }
    await PmaWorkbenchActionsAPI.create(payload);
    isReviewSent.value = true;
    isReviewEditorOpen.value = false;
    reviewDraft.value = '';
    messageReviewBusinessContactDraft.value = {};
    store.dispatch('getConversation', props.conversationId);
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
    useAlert(t('CONVERSATION.PMA_REVIEW_SENT'));
  } catch (error) {
    useAlert(reviewSendFailureMessage(error));
  } finally {
    isReviewSending.value = false;
  }
}

function setToolReviewDraftFromFields() {
  toolReviewDraft.value = toolReviewEditableFields.value.reduce(
    (draft, field) => ({ ...draft, [field.name]: field.value }),
    {}
  );
}

async function openToolReviewEditor() {
  setToolReviewDraftFromFields();
  isToolReviewEditorOpen.value = true;
  await nextTick();
  toolReviewEditor.value?.[0]?.focus?.();
}

function closeToolReviewEditor() {
  if (isToolReviewSending.value) return;

  isToolReviewEditorOpen.value = false;
  toolReviewDraft.value = {};
}

function openDefaultToolReviewEditor() {
  if (
    !shouldOpenToolReviewEditorByDefault.value ||
    isToolReviewEditorOpen.value
  ) {
    return;
  }
  openToolReviewEditor();
}

function toolReviewFailureMessage(error) {
  const payload = error?.response?.data?.error;
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  return t('CONVERSATION.PMA_TOOL_REVIEW_FAILED');
}

async function sendToolReviewAction(actionType) {
  if (isToolReviewSending.value) return;
  if (
    actionType === toolReviewEditSubmitActionType.value &&
    isToolReviewEditSendDisabled.value
  ) {
    return;
  }

  isToolReviewSending.value = true;
  toolReviewAction.value = actionType;
  try {
    const metadata = {};
    const sensitivePayload = {};
    if (actionType === 'va_review_edit_send') {
      toolReviewEditableFields.value.forEach(field => {
        metadata[field.name] = String(toolReviewDraft.value[field.name] ?? '');
      });
    } else if (actionType === 'va_contact_resolution_submit') {
      toolReviewEditableFields.value.forEach(field => {
        const value = String(toolReviewDraft.value[field.name] ?? '');
        if (field.sensitive) {
          sensitivePayload[field.name] = value;
        } else {
          metadata[field.name] = value;
        }
      });
    }
    const payload = {
      chatwoot_conversation_id: props.conversationId,
      chatwoot_message_id: props.id,
      action_type: actionType,
      content:
        actionType === 'va_review_edit_send'
          ? String(toolReviewDraft.value.body ?? '').trim()
          : '',
      metadata,
      idempotency_key: `chatwoot-va-review:${actionType}:${props.id}`,
    };
    if (Object.keys(sensitivePayload).length) {
      payload.sensitive_payload = sensitivePayload;
    }
    await PmaWorkbenchActionsAPI.create(payload);
    isToolReviewCompleted.value = true;
    isToolReviewEditorOpen.value = false;
    toolReviewDraft.value = {};
    store.dispatch('getConversation', props.conversationId);
    useAlert(t('CONVERSATION.PMA_TOOL_REVIEW_SENT'));
  } catch (error) {
    useAlert(toolReviewFailureMessage(error));
  } finally {
    isToolReviewSending.value = false;
    toolReviewAction.value = '';
  }
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

watch(shouldOpenToolReviewEditorByDefault, shouldOpen => {
  if (shouldOpen) openDefaultToolReviewEditor();
});

onMounted(() => {
  setupHighlightTimer();
  openDefaultToolReviewEditor();
});

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  isPmaAiReviewPending,
  isPmaToolReviewPending,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-2 message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-[rgb(var(--whaisper-shell-panel-muted)/0.36)] rounded-[1rem]':
          showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex min-w-0"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <div
          class="flex min-w-0 flex-col"
          :class="{
            'items-end': orientation === ORIENTATION.RIGHT,
            'items-start': orientation === ORIENTATION.LEFT,
            'w-full': variant === MESSAGE_VARIANTS.EMAIL,
          }"
        >
          <Component :is="componentToRender" />
          <div
            v-if="isPmaAiReviewPending"
            class="mt-2 w-[min(32rem,calc(100vw-7rem))] max-w-full rounded-lg border border-n-ruby-7 bg-n-ruby-3 p-3 text-sm text-n-ruby-12"
            @click.stop
            @contextmenu.stop
          >
            <div v-if="!isReviewEditorOpen" class="flex justify-end">
              <button
                type="button"
                class="skip-context-menu inline-flex h-8 items-center rounded-md border border-n-ruby-7 bg-n-ruby-1 px-3 text-xs font-medium text-n-ruby-12 transition-colors hover:bg-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-50"
                @click.stop="openReviewEditor"
              >
                {{ t('CONVERSATION.PMA_REVIEW_EDIT_RESPONSE') }}
              </button>
            </div>
            <div v-else class="space-y-2">
              <label
                class="block text-xs font-medium text-n-ruby-12"
                :for="`pma-review-editor-${props.id}`"
              >
                {{ t('CONVERSATION.PMA_REVIEW_EDITOR_LABEL') }}
              </label>
              <textarea
                :id="`pma-review-editor-${props.id}`"
                ref="reviewEditor"
                v-model="reviewDraft"
                rows="5"
                class="skip-context-menu min-h-32 w-full resize-y rounded-md border border-n-ruby-7 bg-white px-3 py-2 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-ruby-8 focus:ring-2 focus:ring-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isReviewSending"
                @keydown.stop
              />
              <div
                v-if="messageReviewBusinessContactFields.length"
                class="rounded-md border border-n-ruby-6 bg-n-ruby-1 p-3"
              >
                <div
                  class="mb-2 flex items-center gap-2 text-xs font-semibold text-n-ruby-12"
                >
                  <Icon icon="i-lucide-briefcase" class="size-3.5" />
                  {{ t('CONVERSATION.PMA_REVIEW_BUSINESS_CONTACT') }}
                </div>
                <label
                  v-for="field in messageReviewBusinessContactFields"
                  :key="field.name"
                  class="mb-2 block text-xs font-medium text-n-ruby-12 last:mb-0"
                >
                  <span
                    v-if="field.name !== 'save_as_callable_business_contact'"
                  >
                    {{ field.label }}
                  </span>
                  <span
                    v-if="field.name === 'save_as_callable_business_contact'"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="messageReviewBusinessContactDraft[field.name]"
                      type="checkbox"
                      true-value="true"
                      false-value="false"
                      class="skip-context-menu size-4 rounded border-n-ruby-7 text-n-ruby-9 focus:ring-n-ruby-4"
                      :disabled="isReviewSending"
                      @keydown.stop
                    />
                    {{ field.label }}
                  </span>
                  <textarea
                    v-else-if="field.multiline"
                    v-model="messageReviewBusinessContactDraft[field.name]"
                    rows="3"
                    class="skip-context-menu mt-1 min-h-20 w-full resize-y rounded-md border border-n-ruby-7 bg-white px-3 py-2 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-ruby-8 focus:ring-2 focus:ring-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isReviewSending"
                    @keydown.stop
                  />
                  <input
                    v-else-if="
                      field.name !== 'save_as_callable_business_contact'
                    "
                    v-model="messageReviewBusinessContactDraft[field.name]"
                    :type="field.sensitive ? 'tel' : 'text'"
                    class="skip-context-menu mt-1 h-9 w-full rounded-md border border-n-ruby-7 bg-white px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-ruby-8 focus:ring-2 focus:ring-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isReviewSending"
                    autocomplete="off"
                    @keydown.stop
                  />
                </label>
              </div>
              <div class="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  class="skip-context-menu inline-flex h-8 items-center rounded-md border border-n-ruby-7 bg-transparent px-3 text-xs font-medium text-n-ruby-12 transition-colors hover:bg-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isReviewSending"
                  @click.stop="closeReviewEditor"
                >
                  {{ t('CONVERSATION.PMA_REVIEW_CANCEL') }}
                </button>
                <button
                  type="button"
                  class="skip-context-menu inline-flex h-8 items-center rounded-md bg-n-ruby-9 px-3 text-xs font-semibold text-white transition-colors hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isReviewSendDisabled"
                  @click.stop="sendReviewedMessage"
                >
                  {{
                    isReviewSending
                      ? t('CONVERSATION.PMA_REVIEW_SENDING')
                      : t('CONVERSATION.PMA_REVIEW_SEND_TO_USER')
                  }}
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="isPmaToolReviewPending"
            class="mt-2 w-[min(34rem,calc(100vw-7rem))] max-w-full rounded-lg border border-n-amber-7 bg-n-amber-3 p-3 text-sm text-n-amber-12"
            @click.stop
            @contextmenu.stop
          >
            <div class="mb-3 flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="text-xs font-medium uppercase text-n-amber-11">
                  {{ t('CONVERSATION.PMA_TOOL_REVIEW_PENDING') }}
                </div>
                <div class="truncate text-sm font-semibold">
                  {{ toolReviewDisplayName }}
                </div>
              </div>
              <Icon
                icon="i-lucide-shield-check"
                class="mt-0.5 size-4 shrink-0"
              />
            </div>
            <div
              v-if="!isToolReviewEditorOpen"
              class="flex flex-wrap justify-end gap-2"
            >
              <button
                v-tooltip.top="t('CONVERSATION.PMA_TOOL_REVIEW_REJECT')"
                type="button"
                class="skip-context-menu inline-flex h-8 items-center gap-1 rounded-md border border-n-ruby-7 bg-n-ruby-1 px-3 text-xs font-medium text-n-ruby-12 transition-colors hover:bg-n-ruby-4 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isToolReviewSending"
                @click.stop="sendToolReviewAction('va_review_reject')"
              >
                <Icon
                  :icon="
                    isToolReviewSending &&
                    toolReviewAction === 'va_review_reject'
                      ? 'i-lucide-loader-circle'
                      : 'i-lucide-x'
                  "
                  class="size-3.5"
                  :class="{
                    'animate-spin':
                      isToolReviewSending &&
                      toolReviewAction === 'va_review_reject',
                  }"
                />
                {{ t('CONVERSATION.PMA_TOOL_REVIEW_REJECT') }}
              </button>
              <button
                v-if="toolReviewEditableFields.length"
                type="button"
                class="skip-context-menu inline-flex h-8 items-center gap-1 rounded-md border border-n-amber-7 bg-transparent px-3 text-xs font-medium text-n-amber-12 transition-colors hover:bg-n-amber-4 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isToolReviewSending"
                @click.stop="openToolReviewEditor"
              >
                <Icon icon="i-lucide-pen-line" class="size-3.5" />
                {{ t('CONVERSATION.PMA_TOOL_REVIEW_EDIT') }}
              </button>
              <button
                v-if="!isToolReviewContactResolution"
                type="button"
                class="skip-context-menu inline-flex h-8 items-center gap-1 rounded-md bg-n-amber-9 px-3 text-xs font-semibold text-white transition-colors hover:bg-n-amber-10 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isToolReviewSending"
                @click.stop="sendToolReviewAction('va_review_approve')"
              >
                <Icon
                  :icon="
                    isToolReviewSending &&
                    toolReviewAction === 'va_review_approve'
                      ? 'i-lucide-loader-circle'
                      : 'i-lucide-check'
                  "
                  class="size-3.5"
                  :class="{
                    'animate-spin':
                      isToolReviewSending &&
                      toolReviewAction === 'va_review_approve',
                  }"
                />
                {{ t('CONVERSATION.PMA_TOOL_REVIEW_APPROVE') }}
              </button>
            </div>
            <div v-else class="space-y-2">
              <label
                v-for="field in toolReviewEditableFields"
                :key="field.name"
                class="block text-xs font-medium text-n-amber-12"
              >
                <span>{{ field.label }}</span>
                <textarea
                  v-if="field.multiline"
                  ref="toolReviewEditor"
                  v-model="toolReviewDraft[field.name]"
                  rows="5"
                  class="skip-context-menu mt-1 min-h-32 w-full resize-y rounded-md border border-n-amber-7 bg-white px-3 py-2 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-amber-8 focus:ring-2 focus:ring-n-amber-4 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isToolReviewSending"
                  @keydown.stop
                />
                <input
                  v-else
                  ref="toolReviewEditor"
                  v-model="toolReviewDraft[field.name]"
                  :type="field.sensitive ? 'tel' : 'text'"
                  class="skip-context-menu mt-1 h-9 w-full rounded-md border border-n-amber-7 bg-white px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-amber-8 focus:ring-2 focus:ring-n-amber-4 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isToolReviewSending"
                  autocomplete="off"
                  @keydown.stop
                />
              </label>
              <div class="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  class="skip-context-menu inline-flex h-8 items-center gap-1 rounded-md border border-n-amber-7 bg-transparent px-3 text-xs font-medium text-n-amber-12 transition-colors hover:bg-n-amber-4 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isToolReviewSending"
                  @click.stop="closeToolReviewEditor"
                >
                  <Icon icon="i-lucide-undo-2" class="size-3.5" />
                  {{ t('CONVERSATION.PMA_REVIEW_CANCEL') }}
                </button>
                <button
                  type="button"
                  class="skip-context-menu inline-flex h-8 items-center gap-1 rounded-md bg-n-amber-9 px-3 text-xs font-semibold text-white transition-colors hover:bg-n-amber-10 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isToolReviewEditSendDisabled"
                  @click.stop="
                    sendToolReviewAction(toolReviewEditSubmitActionType)
                  "
                >
                  <Icon
                    :icon="
                      isToolReviewSending &&
                      toolReviewAction === toolReviewEditSubmitActionType
                        ? 'i-lucide-loader-circle'
                        : 'i-lucide-send'
                    "
                    class="size-3.5"
                    :class="{
                      'animate-spin':
                        isToolReviewSending &&
                        toolReviewAction === toolReviewEditSubmitActionType,
                    }"
                  />
                  {{
                    isToolReviewSending &&
                    toolReviewAction === toolReviewEditSubmitActionType
                      ? t('CONVERSATION.PMA_REVIEW_SENDING')
                      : t('CONVERSATION.PMA_TOOL_REVIEW_EDIT_SEND')
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}
</style>
